import React, { useState, useRef } from 'react';
import { Text, Button, Stack, Image, Box } from '@mantine/core';
import { createStyles } from '@mantine/styles';
import { IconUpload, IconPhoto, IconArrowBack } from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
  dropzone: {
    width: '100%',
    maxWidth: '500px',
    height: '350px',
    border: '3px dashed rgba(203, 213, 225, 0.8)',
    borderRadius: 'var(--border-radius)',
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(8px)',
    cursor: 'pointer',
    transition: 'var(--transition)',
    boxShadow: 'var(--shadow-sm)',
    margin: '0 auto',
    '[data-theme="dark"] &': {
      backgroundColor: 'rgba(30, 41, 59, 0.5)',
      border: '3px dashed rgba(71, 85, 105, 0.8)',
    },
    '&:hover': {
      borderColor: 'var(--primary-color)',
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      transform: 'translateY(-5px)',
      boxShadow: 'var(--shadow-md)',
      '[data-theme="dark"] &': {
        backgroundColor: 'rgba(30, 41, 59, 0.8)',
      },
    },
  },
  uploadIcon: {
    color: 'var(--text-light)',
    marginBottom: '20px',
    transition: 'var(--transition)',
    opacity: 0.8,
  },
  imageContainer: {
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto',
    position: 'relative',
    borderRadius: 'var(--border-radius)',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-md)',
    border: '4px solid white',
    transition: 'var(--transition)',
    animation: 'fadeIn 0.5s ease-out',
    '[data-theme="dark"] &': {
      border: '4px solid var(--bg-card)',
    },
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: 'var(--shadow-lg)',
    },
  },
  actionButton: {
    height: '48px',
    fontWeight: 500,
    padding: '0 24px',
    borderRadius: '24px',
    transition: 'var(--transition)',
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-sm)',
    },
  },
  successButton: {
    background: 'linear-gradient(45deg, var(--success-color), #38bdf8)',
    color: 'white',
    fontWeight: 600,
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover': {
      background: 'linear-gradient(45deg, #22c55e, #0ea5e9)',
      boxShadow: 'var(--shadow-md)',
    },
  },
  buttonContainer: {
    margin: '24px auto 0',
    width: '100%',
    maxWidth: '500px',
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
  },
  uploadText: {
    fontSize: '1.2rem',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    textAlign: 'center',
    marginBottom: '8px',
    transition: 'var(--transition)',
  },
  uploadSubtext: {
    fontSize: '0.9rem',
    color: 'var(--text-light)',
    textAlign: 'center',
    maxWidth: '300px',
    lineHeight: 1.5,
  },
}));

const ImageUpload = ({ onImageUpload }) => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const fileInputRef = useRef(null);
  const { classes } = useStyles();

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      handleFileUpload(file);
    }
  };

  const handleFileUpload = (file) => {
    if (file && file.type.match('image.*')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result;
        console.log('Image loaded:', result.substring(0, 50) + '...'); // Debug log
        setUploadedImage(result);
      };
      reader.readAsDataURL(file);
    } else {
      console.error('Invalid file type:', file?.type);
    }
  };

  const handleInputChange = (e) => {
    console.log('File input change detected'); // Debug log
    if (e.target.files && e.target.files[0]) {
      console.log('File selected:', e.target.files[0].name); // Debug log
      handleFileUpload(e.target.files[0]);
    }
  };

  const confirmImage = () => {
    if (uploadedImage && onImageUpload) {
      console.log('Confirming image use'); // Debug log
      onImageUpload(uploadedImage);
    }
  };

  const cancelUpload = () => {
    setUploadedImage(null);
  };

  const openFileDialog = () => {
    console.log('Opening file dialog'); // Debug log
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Stack align="center" spacing="md" style={{ width: '100%' }}>
      {!uploadedImage ? (
        <div
          className={classes.dropzone}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={openFileDialog}
        >
          <input
            type="file"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleInputChange}
            accept="image/*"
          />
          <IconUpload size={64} className={classes.uploadIcon} stroke={1.5} />
          <Text className={classes.uploadText}>
            Click to upload or drag and drop
          </Text>
          <Text className={classes.uploadSubtext}>
            Supported formats: JPG, PNG, GIF
            <br />
            Maximum size: 5MB
          </Text>
        </div>
      ) : (
        <>
          <div className={classes.imageContainer}>
            <Image
              src={uploadedImage}
              alt="Uploaded image"
              width="100%"
              fit="contain"
              radius="md"
            />
          </div>
          <Box className={classes.buttonContainer}>
            <Button
              leftSection={<IconArrowBack size={18} />}
              variant="outline"
              color="gray"
              onClick={cancelUpload}
              className={classes.actionButton}
            >
              Back
            </Button>
            <Button
              leftSection={<IconPhoto size={18} />}
              onClick={confirmImage}
              className={`${classes.actionButton} ${classes.successButton}`}
            >
              Use This Photo
            </Button>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default ImageUpload;
